import { useCallback } from 'react';

// Hover-intent delay (ms) before opening a submenu, so a fast pass-over
// of an item with children doesn't briefly flash its submenu open.
const SUBMENU_OPEN_DELAY = 50;

export const usePopoverHandlers = ({
  setIsOpen,
  setHoveredIndexes,
  timeoutRef,
  enterTimeoutRef,
  shouldCloseOnHover,
  popoverContext,
  onOptionClick,
  disableBlur,
  panelRef,
  submenuRefs
}) => {
  const handleMouseEnter = useCallback(
    ({ level, option, index }) => {
      if (!option.children?.length && option.disabled) return;

      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      // Cancel any pending submenu-open scheduled by a previously hovered sibling.
      if (enterTimeoutRef.current) clearTimeout(enterTimeoutRef.current);

      const applyHover = () => {
        setHoveredIndexes((prev) => {
          if (prev[level] !== index) {
            // Close levels higher than current level when entering any level
            return {
              ...Object.fromEntries(Object.entries(prev).filter(([key]) => parseInt(key) <= level)),
              [level]: index
            };
          }
          return prev;
        });
      };

      // Delay opening only for items that actually reveal a submenu; leaf items
      // highlight instantly so the menu still feels responsive.
      if (option.children?.length) {
        enterTimeoutRef.current = setTimeout(applyHover, SUBMENU_OPEN_DELAY);
      } else {
        applyHover();
      }
    },
    [setHoveredIndexes, timeoutRef, enterTimeoutRef]
  );

  const handleMouseLeave = useCallback(
    ({ level, option, event }) => {
      if (!shouldCloseOnHover || (!option.children?.length && option.disabled)) return;

      // Check if mouse is still within any relevant panel
      const isMouseInMainPanel =
        event?.relatedTarget &&
        event.relatedTarget instanceof Node &&
        panelRef?.current?.contains(event.relatedTarget);

      // For any open submenu (including the one being left), check whether the
      // mouse is moving into another open submenu node. submenuRefs.current
      // stores DOM nodes directly (set by handleSubmenuPositioning).
      let isMouseInSubmenu = false;
      if (submenuRefs?.current && event?.relatedTarget instanceof Node) {
        for (const submenuNode of Object.values(submenuRefs.current)) {
          if (submenuNode?.contains?.(event.relatedTarget)) {
            isMouseInSubmenu = true;
            break;
          }
        }
      }

      const isMouseStillInPanel = isMouseInMainPanel || isMouseInSubmenu;

      if (isMouseStillInPanel) {
        // Mouse is still within a panel, don't close immediately
        return;
      } else {
        // Mouse left all panels, apply 500ms delay before closing
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        // Cancel any submenu-open still waiting on its hover-intent delay.
        if (enterTimeoutRef?.current) clearTimeout(enterTimeoutRef.current);

        const timeout = (timeoutRef.current = setTimeout(() => {
          setHoveredIndexes((prev) => ({ ...prev, [level]: null }));
        }, 500));

        return () => clearTimeout(timeout);
      }
    },
    [shouldCloseOnHover, setHoveredIndexes, timeoutRef, enterTimeoutRef, panelRef, submenuRefs]
  );

  const handleOptionClick = useCallback(
    (option, handleClose, event) => {
      if (option.onClick) {
        event.stopPropagation();
        option.onClick(popoverContext?.context);

        if (!option.preventClose) {
          handleClose();
          setIsOpen(false);
        }
      }
      if (onOptionClick) {
        onOptionClick(option, handleClose);
      }
    },
    [popoverContext, onOptionClick, setIsOpen]
  );

  const onPopoverClose = useCallback(
    (event) => {
      if (event?.type === 'blur' && disableBlur) return;
      setIsOpen(false);
    },
    [disableBlur, setIsOpen]
  );

  return {
    handleMouseEnter,
    handleMouseLeave,
    handleOptionClick,
    onPopoverClose
  };
};
